import { orderBboxItemsByHilbertCurve } from "utils/math/orderBboxItemsByHilbertCurve";
import { resolveGroupColor } from "../resolveGroupColor";
import type { GroupedSmartSurveyorElement } from "components/SmartSurveyor/ReviewPage/types";
import type { BulkMovePlanElements } from "./types";

export const bulkMovePlanElements: BulkMovePlanElements = ({
  plan,
  elementIds,
  toGroupLabel,
  newGroupElementTypeId,
}) => {
  const idsToMove = new Set(elementIds);
  const byGroupId = { ...plan.groupedElements.byGroupId };
  const emptiedGroupLabels = new Set<string>();
  const movedElements: GroupedSmartSurveyorElement[] = [];

  for (const groupLabel of Object.keys(byGroupId)) {
    if (groupLabel === toGroupLabel) {
      continue;
    }

    const groupElements = byGroupId[groupLabel];
    const stayingElements = groupElements.filter(
      (element) => !idsToMove.has(element.id),
    );

    if (stayingElements.length === groupElements.length) {
      continue;
    }

    movedElements.push(
      ...groupElements.filter((element) => idsToMove.has(element.id)),
    );

    if (stayingElements.length === 0) {
      emptiedGroupLabels.add(groupLabel);
      delete byGroupId[groupLabel];
    } else {
      byGroupId[groupLabel] = stayingElements;
    }
  }

  if (movedElements.length === 0) {
    return plan;
  }

  const targetGroupColor = resolveGroupColor({
    groupName: toGroupLabel,
    byGroupId: plan.groupedElements.byGroupId,
  });

  const recoloredMovedElements = movedElements.map((element) => ({
    ...element,
    group_id: toGroupLabel,
    rgb: targetGroupColor,
  }));

  byGroupId[toGroupLabel] = orderBboxItemsByHilbertCurve({
    elements: [
      ...(plan.groupedElements.byGroupId[toGroupLabel] ?? []),
      ...recoloredMovedElements,
    ],
  });

  let groupIds = plan.groupedElements.groupIds.filter(
    (groupLabel) => !emptiedGroupLabels.has(groupLabel),
  );
  if (!groupIds.includes(toGroupLabel)) {
    groupIds = [...groupIds, toGroupLabel];
  }

  let groupMeta = plan.groupMeta;
  if (emptiedGroupLabels.size > 0) {
    groupMeta = { ...plan.groupMeta };
    for (const emptiedGroupLabel of emptiedGroupLabels) {
      delete groupMeta[emptiedGroupLabel];
    }
  }

  if (!groupMeta[toGroupLabel]) {
    groupMeta = {
      ...groupMeta,
      [toGroupLabel]: {
        element_type_id: newGroupElementTypeId,
        // Matches backend: element types created via bulk-move are CONFIDENT
        nameStatus: "confident",
      },
    };
  }

  return {
    ...plan,
    groupedElements: {
      ...plan.groupedElements,
      byGroupId,
      groupIds,
    },
    groupMeta,
  };
};
